<template>
  <!--  窗体代码  -->
  <div class="login" :style="loginBgStyle">
    <Vcode
      :show="showCaptcha"
      :imgs="captchaImages"
      :slider-size="46"
      @success="handleCaptchaSuccess"
      @fail="handleCaptchaFail"
      @close="handleCaptchaClose"
    />
    <el-container>
      <el-main>
        <!--  登录表单窗体  -->
        <el-row class="login-form">
          <!--  登陆表单左面板 -->
          <!--  登陆表单右面板 -->
          <el-col :span="24" class="login-form-right">
            <!--  登录表单  -->
            <el-form
              ref="loginFormRef"
              :model="loginForm"
              :rules="loginRules"
              @keyup.enter="triggerLogin(loginFormRef)"
            >
              <el-form-item prop="username">
                <el-input
                  v-model="loginForm.username"
                  placeholder="用户名"
                ></el-input>
              </el-form-item>
              <el-form-item prop="password">
                <el-input
                  type="password"
                  v-model="loginForm.password"
                  placeholder="密码"
                ></el-input>
              </el-form-item>
              <el-form-item>
                <el-button
                  type="primary"
                  @click="triggerLogin(loginFormRef)"
                  ref="loginButtonRef"
                  >登录</el-button
                >
              </el-form-item>
            </el-form>
          </el-col>
        </el-row>
      </el-main>
    </el-container>
  </div>
</template>

<script lang="ts" setup>
import { reactive, ref, nextTick } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import { ElMessageBox, ElButton } from "element-plus";
import jsCookie from "js-cookie";
import router from "@/router";
import config from "@/config";
import { logger } from "@/utils/logger";
import { useStore } from "vuex";
import apiClient from "@/services/apiClient";
import websocketManager from "@/services/websocketManager";
import backgroundImage from "@/assets/images/background.jpeg";
import Vcode from "vue3-puzzle-vcode";

const loginBgStyle = {
  backgroundImage: `url(${backgroundImage})`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "left center",
  backgroundSize: "60%",
  backgroundColor: "#9cd4e1",
};

// 中文注释：引入全局store
const store = useStore();

// 判断用户登录状态
const userLoginState = jsCookie.get("username");
if (userLoginState != undefined) {
  router.replace("/dashboard").catch((err) => {
    console.error("路由跳转失败:", err);
    console.log("当前路由信息:", router.currentRoute.value);
    console.log("Token 状态:", jsCookie.get("userToken"));
  });
}

// 登录表单
const loginButtonRef = ref();
let loginState = ref(false);
const loginFormRef = ref<FormInstance>();
const loginForm = reactive({
  username: localStorage.getItem("lastLoginUsername") || "",
  password: "",
});

// 滑动拼图验证码相关
const showCaptcha = ref(false);
const captchaPassed = ref(false);
const captchaImages = Array.from(
  { length: 9 },
  (_, idx) => `/vcode/tiles/vcode_${idx + 1}.jpg`
);

const handleCaptchaSuccess = () => {
  showCaptcha.value = false;
  captchaPassed.value = true;
  login(loginFormRef.value);
};

const handleCaptchaFail = () => {
  captchaPassed.value = false;
};

const handleCaptchaClose = () => {
  showCaptcha.value = false;
};

const triggerLogin = (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  formEl.validate((valid) => {
    if (valid) {
      if (captchaPassed.value) {
        login(formEl);
      } else {
        showCaptcha.value = true;
      }
    }
  });
};

// 表单校验
const loginRules = reactive<FormRules>({
  username: [{ required: true, message: "请输入用户名", trigger: "blur" }],
  password: [{ required: true, message: "请输入密码", trigger: "blur" }],
});
// const httpsAgent = new https.Agent({ rejectUnauthorized: false });

// 登录操作（重构，登录后拉取用户详细信息并存入store）
const login = (formEl: FormInstance | undefined) => {
  if (!formEl || loginState.value) return;
  formEl.validate((valid) => {
    if (valid) {
      loginState.value = true;
      const url = config.apiUrls.LOGIN;
      apiClient
        .post(url, loginForm)
        .then((response) => {
          // 修复：apiClient返回完整response，需解构 response.data.data
          const { token, userInfo } = response.data.data;

          if (!token || !userInfo || !userInfo.role) {
            ElMessageBox.alert(
              "登录响应格式错误，缺少token或用户信息",
              "错误",
              {
                confirmButtonText: "确定",
              }
            );
            store.dispatch("logout"); // 修复：响应格式错误时清理状态
            loginState.value = false;
            return;
          }

          localStorage.setItem("lastLoginUsername", loginForm.username);
          jsCookie.set("username", userInfo.username, {
            expires: 1,
            path: "/",
          });
          jsCookie.set("userToken", token, { expires: 1, path: "/" });

          store.commit("setUser", userInfo);
          if (userInfo && (userInfo as any).tenant_id !== undefined) {
            jsCookie.set("tenantId", String((userInfo as any).tenant_id), {
              expires: 1,
              path: "/",
            });
          }

          // 登录成功后，通过userinfo接口获取完整的用户信息（包含角色等）
          store
            .dispatch("fetchUserInfo")
            .then(() => {
              console.log("用户信息获取成功");
              // 先断开可能由 autoConnect 建立的旧连接，避免沿用旧的租户ID
              websocketManager.disconnect();
              websocketManager.connect(token, userInfo.username);
            })
            .catch((error) => {
              console.error("获取用户信息失败:", error);
              // 即使获取用户信息失败，也不影响登录流程
            });

          // VERTC SDK 已在 index.html 中同步加载，无需动态加载

          nextTick(() => {
            router.replace("/dashboard").catch((err) => {
              logger.error("路由跳转失败:", err);
            });
          });
        })
        .catch((error) => {
          const backendMessage =
            error.response?.data?.message ||
            error.response?.data?.detail ||
            error.response?.data?.error ||
            error.message;
          const errorMessage = backendMessage || "网络错误或服务器无响应";
          ElMessageBox.alert(errorMessage, "登录失败", {
            confirmButtonText: "确定",
          });
          store.dispatch("logout"); // 修复：捕获到任何错误时都清理状态
          loginState.value = false;
        });
    } else {
      if (!valid) {
        console.log("表单校验失败");
      }
      loginState.value = false;
    }
  });
};
</script>

<style lang="scss">
@import "../assets/css/login";
</style>
